import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { LOGO_URL, LOGO_CREAM_URL } from "@/data/content";

const Navbar = () => {
  const { t, lang, setLanguage } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Header is always visible with a solid cream backdrop so the logo and
  // navigation read clearly from the very top (no transparent hero state).
  const heroMode = false;

  const links = [
    { to: "/about", label: t.nav.about, id: "about" },
    { to: "/services", label: t.nav.services, id: "services" },
    { to: "/projects", label: t.nav.projects, id: "projects" },
    { to: "/contact", label: t.nav.contact, id: "contact" },
  ];

  const linkClass = ({ isActive }) =>
    `text-xs uppercase tracking-[0.18em] transition-colors duration-300 ${
      heroMode
        ? isActive
          ? "text-cream"
          : "text-cream/75 hover:text-cream"
        : isActive
        ? "text-olive"
        : "text-charcoal/70 hover:text-sage"
    }`;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 bg-cream/90 backdrop-blur-xl ${
        scrolled ? "border-b border-border-subtle" : "border-b border-border-subtle/60"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <Link
            to="/"
            data-testid="nav-logo-link"
            aria-label="BY VAYA home"
            className="flex items-center"
          >
            <img
              src={heroMode ? LOGO_CREAM_URL : LOGO_URL}
              alt="BY VAYA"
              className="h-9 md:h-11 w-auto object-contain"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-10">
            {links.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                className={linkClass}
                data-testid={`nav-${l.id}-link`}
              >
                {l.label}
              </NavLink>
            ))}

            <div
              className={`flex items-center gap-3 pl-6 ml-2 border-l ${
                heroMode ? "border-cream/30" : "border-border-subtle"
              }`}
              data-testid="lang-toggle"
            >
              {["en", "de"].map((code) => (
                <button
                  key={code}
                  onClick={() => setLanguage(code)}
                  data-testid={`lang-${code}-btn`}
                  className={`text-xs uppercase tracking-[0.18em] transition-colors duration-300 ${
                    lang === code
                      ? heroMode
                        ? "text-cream"
                        : "text-olive"
                      : heroMode
                      ? "text-cream/55 hover:text-cream"
                      : "text-taupe hover:text-sage"
                  }`}
                >
                  {code}
                </button>
              ))}
            </div>

            <Link
              to="/contact"
              data-testid="nav-work-cta"
              className={`text-xs uppercase tracking-[0.18em] transition-colors duration-300 px-6 py-3 ${
                heroMode
                  ? "bg-cream text-charcoal hover:bg-sage hover:text-cream"
                  : "bg-olive text-cream hover:bg-charcoal"
              }`}
            >
              {t.nav.work}
            </Link>
          </nav>

          {/* Mobile toggle */}
          <button
            className={`md:hidden ${heroMode ? "text-cream" : "text-charcoal"}`}
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="md:hidden bg-cream border-t border-border-subtle"
          data-testid="mobile-menu"
        >
          <nav className="flex flex-col px-6 py-8 gap-6">
            {links.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                className="font-serif text-2xl text-charcoal"
                data-testid={`mobile-nav-${l.id}-link`}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex items-center gap-3 pt-4">
              {["en", "de"].map((code) => (
                <button
                  key={code}
                  onClick={() => setLanguage(code)}
                  data-testid={`mobile-lang-${code}-btn`}
                  className={`text-xs uppercase tracking-[0.18em] ${
                    lang === code ? "text-olive" : "text-taupe"
                  }`}
                >
                  {code}
                </button>
              ))}
            </div>
            <Link
              to="/contact"
              className="text-xs uppercase tracking-[0.18em] text-cream bg-olive px-6 py-4 text-center mt-2"
              data-testid="mobile-nav-work-cta"
            >
              {t.nav.work}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
